import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Card, Col, Drawer, Empty, Form, Input, InputNumber, message,
  Popconfirm, Row, Space, Switch, Tag, Typography,
} from "antd";
import { useState } from "react";
import {
  createSwiper, deleteSwiper, listSwipersAdmin, updateSwiper,
  type SwiperOut,
} from "./api";

export function SwipersPage() {
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();
  const [openCreate, setOpenCreate] = useState(false);
  const [editOne, setEditOne] = useState<SwiperOut | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const listQ = useQuery({ queryKey: ["swipers-admin"], queryFn: listSwipersAdmin });
  const reload = () => qc.invalidateQueries({ queryKey: ["swipers-admin"] });

  const createM = useMutation({
    mutationFn: createSwiper,
    onSuccess: () => { msgApi.success("已创建"); setOpenCreate(false); createForm.resetFields(); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const updateM = useMutation({
    mutationFn: ({ sid, body }: { sid: string; body: Omit<SwiperOut, "id"> }) =>
      updateSwiper(sid, body),
    onSuccess: () => { msgApi.success("已更新"); setEditOne(null); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const delM = useMutation({
    mutationFn: deleteSwiper,
    onSuccess: () => { msgApi.success("已删除"); reload(); },
  });

  const formLayout = (
    <>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="title" label="中文标题">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="title_en" label="英文标题">
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="image_url" label="图片 URL" rules={[{ required: true }]}>
        <Input placeholder="https://..." />
      </Form.Item>
      <Form.Item name="link_url" label="点击跳转 URL">
        <Input />
      </Form.Item>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="order_num" label="排序(升序)" initialValue={0}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="content" label="中文介绍">
        <Input.TextArea rows={2} />
      </Form.Item>
      <Form.Item name="content_en" label="英文介绍">
        <Input.TextArea rows={2} />
      </Form.Item>
    </>
  );

  return (
    <div>
      {msgCtx}
      <Card
        title="轮播图管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />}
            onClick={() => { createForm.resetFields(); setOpenCreate(true); }}>
            新增轮播
          </Button>
        }
      >
        {listQ.data?.length === 0 && <Empty />}
        <Row gutter={[12, 12]}>
          {(listQ.data ?? []).map(s => (
            <Col key={s.id} xs={24} md={12} xl={8}>
              <Card
                size="small"
                cover={
                  s.image_url ? (
                    <img
                      src={s.image_url}
                      alt={s.title || ""}
                      style={{ width: "100%", height: 160, objectFit: "cover" }}
                    />
                  ) : (
                    <div style={{
                      width: "100%", height: 160, background: "#f0f0f0",
                      display: "flex", alignItems: "center", justifyContent: "center", color: "#999",
                    }}>无图</div>
                  )
                }
                actions={[
                  <EditOutlined key="e"
                    onClick={() => { editForm.setFieldsValue(s); setEditOne(s); }}
                  />,
                  <Popconfirm key="d" title="删除?" onConfirm={() => delM.mutate(s.id)}>
                    <DeleteOutlined />
                  </Popconfirm>,
                ]}
              >
                <Card.Meta
                  title={
                    <Space>
                      <span>{s.title || "(无标题)"}</span>
                      {s.is_active ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>}
                      <Tag>排序 {s.order_num}</Tag>
                    </Space>
                  }
                  description={
                    s.content ? (
                      // 对齐旧站 inxSwiper 列表的 v-html:content 为后台录入的受信富文本,按 HTML 渲染
                      <div
                        style={{
                          margin: 0, maxHeight: 44, overflow: "hidden",
                          color: "rgba(0, 0, 0, 0.45)", fontSize: 14,
                        }}
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: 等价旧站 v-html,内容为后台 swipers 管理端录入的受信 HTML
                        dangerouslySetInnerHTML={{ __html: s.content }}
                      />
                    ) : (
                      <Typography.Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ margin: 0 }}>
                        {s.title_en || "—"}
                      </Typography.Paragraph>
                    )
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      <Drawer open={openCreate} title="新增轮播" width={560} onClose={() => setOpenCreate(false)}>
        <Form form={createForm} layout="vertical" onFinish={(v) => createM.mutate(v)}>
          {formLayout}
          <Button type="primary" htmlType="submit" block loading={createM.isPending}>创建</Button>
        </Form>
      </Drawer>

      <Drawer open={!!editOne} title="编辑轮播" width={560} onClose={() => setEditOne(null)}>
        <Form form={editForm} layout="vertical"
          onFinish={(v) => editOne && updateM.mutate({ sid: editOne.id, body: v })}>
          {formLayout}
          <Space>
            <Button type="primary" htmlType="submit" loading={updateM.isPending}>保存</Button>
            <Button onClick={() => setEditOne(null)}>取消</Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
}
